import { storeToRefs } from "pinia"
import { setup, createActor, fromPromise } from "xstate"
import type { MoveMachineEventType } from "./types"
import router from "@/router"
import { useAnswersStore } from "@/features/move/store/answers.store"
import { useQuestionsStore } from "@/features/move/store/questions.store"
import { useUserStore } from "@/stores/user"
import { globalActor } from "@/machines/globalMachine"
import { useI18nStore } from "@/stores/i18n"
import { useCookies } from "@vueuse/integrations/useCookies.js"
import { nextTick } from "vue"
import type { GlobalMachineEvent } from "@/machines/types"
import { COOKIES } from "@/config/constants"

// Loader for Drive datas
// to redirect to Intro or WelcomeBack
const loadMoveData = fromPromise(async () => {
  const i18nStore = useI18nStore()
  const { fetchMove, initMove, retakeTest } = useAnswersStore()
  const move = await fetchMove()

  const { fetchQuestions, fetchAudios } = useQuestionsStore()
  const { questionsLength } = storeToRefs(useQuestionsStore())
  const { assessmentPE } = storeToRefs(useUserStore())
  const { updateLocale, isCurrentLangFr } = i18nStore

  const currentMove = assessmentPE.value?.assessments?.move

  if (
    move.status === "todo" ||
    move.status === "inprogress" ||
    move.status === "finish"
  ) {
    const assessmentSummary = useUserStore().assessmentPE.assessments.move
    const isFinisedAndNotPassed =
      move?.status === "finish" && assessmentSummary?.not_passed

    // Legacy, the test has been not_passed and considered with status finish in assessment summary (old) but not in workflow and next_assessment (new)
    let assessmentStatus = isFinisedAndNotPassed ? "todo" : move?.status

    // status can be "todo", "inprogress" or "finish"
    switch (assessmentStatus) {
      case "todo":
        if (isCurrentLangFr()) {
          await updateLocale(1) // Force FR
        } else {
          await updateLocale(3) // Force EN for other langs
        }

        await fetchQuestions()
        await fetchAudios()
        return { status: "new" }

      case "inprogress":
        await fetchQuestions()
        await fetchAudios()
        // If there is less answers than questions, we need to show the questions
        if (move.data.items.length < questionsLength.value) {
          return { status: "showQuestions" }
        }
        // If there is no native language, we need to show the native language screen
        else if (
          move.data.extra?.native_language == undefined ||
          move.data.extra?.native_language == null
        ) {
          return { status: "showNativeLanguage" }
        }
        // If there is no wholesome conditions, we need to show the wholesome conditions screen
        else if (
          move.data.extra?.wholesome_conditions == undefined ||
          move.data.extra?.wholesome_conditions == null
        ) {
          return { status: "showWholesomeConditions" }
        }
        // If the user has complete every steps but status is inprogress, we need to send the results to backend
        else {
          return { status: "showLoadResults" }
        }

      case "finish":
        if (currentMove?.retake_test) {
          await retakeTest()
          await fetchQuestions()
          await fetchAudios()
          return { status: "new" }
        }
        return { status: "showFinal" }

      default:
        await fetchQuestions()
        await fetchAudios()
        return { status: "new" }
    }
  } else {
    await initMove()
    await fetchQuestions()
    await fetchAudios()
    return { status: "new" }
  }
})

export const moveMachine = setup({
  actors: {
    loadMoveData,
  },
  types: {} as {
    events: MoveMachineEventType
  },
  actions: {},
}).createMachine({
  id: "moveMachine",
  initial: "loading",
  states: {
    loading: {
      // entry: [() => router.replace({ name: "moveLoading" })],
      entry: [
        () => {
          if (router.currentRoute.value.name !== "moveLoading") {
            router.replace({ name: "moveLoading" })
          }
        },
      ],
      invoke: {
        id: "loadMove",
        src: "loadMoveData",
        onDone: [
          {
            guard: ({ event }) =>
              (event.output as { status: string })?.status === "new",
            target: "intro",
          },
          {
            guard: ({ event }) =>
              (event.output as { status: string })?.status === "showQuestions",
            target: "welcomeBack",
          },
          {
            guard: ({ event }) =>
              (event.output as { status: string })?.status ===
              "showNativeLanguage",
            target: "nativeLanguage",
          },
          {
            guard: ({ event }) =>
              (event.output as { status: string })?.status ===
              "showWholesomeConditions",
            target: "wholesomeConditions",
          },
          {
            guard: ({ event }) =>
              (event.output as { status: string })?.status ===
              "showLoadResults",
            target: "loadResults",
          },
          {
            guard: ({ event }) =>
              (event.output as { status: string })?.status === "showFinal",
            target: "final",
          },
        ],
      },
    },
    intro: {
      entry: [() => router.replace({ name: "moveIntro" })],
      on: {
        done: { target: "rules" },
      },
    },
    welcomeBack: {
      entry: [() => router.replace({ name: "moveWelcomeBack" })],
      on: {
        done: { target: "test" },
      },
    },
    rules: {
      entry: [() => router.replace({ name: "moveRules" })],
      on: {
        done: { target: "practice" },
        back: { target: "intro" },
      },
    },
    practice: {
      entry: [() => router.replace({ name: "movePractice" })],
      on: {
        done: { target: "tuto" },
        back: { target: "rules" },
      },
    },
    tuto: {
      entry: [() => router.replace({ name: "moveTuto" })],
      on: {
        done: { target: "tutoEnd" },
      },
    },
    tutoEnd: {
      entry: [() => router.replace({ name: "moveTutoEnd" })],
      on: {
        done: { target: "test" },
        back: { target: "tuto" },
      },
    },
    test: {
      entry: [() => router.replace({ name: "moveTest" })],
      on: {
        done: { target: "nativeLanguage" },
      },
    },
    nativeLanguage: {
      entry: [() => router.replace({ name: "moveNativeLanguage" })],
      on: {
        done: { target: "wholesomeConditions" },
      },
    },
    wholesomeConditions: {
      entry: [() => router.replace({ name: "moveWholesomeConditions" })],
      on: {
        true: { target: "loadResults" },
        false: { target: "loadResults" },
      },
    },
    loadResults: {
      entry: [() => router.replace({ name: "moveLoadResults" })],
      on: {
        done: { target: "final" },
      },
    },
    final: {
      type: "final",
    },
  },
})

export const moveActor = createActor(moveMachine)

moveActor.subscribe({
  async complete() {
    const { fetchNextAssessment } = useUserStore()
    const { nextAssessmentName } = storeToRefs(useUserStore())

    const pm_uuid = useCookies().get(COOKIES.PM_UUID)
    const company_uuid = useCookies().get(COOKIES.COMPANY_UUID)
    await fetchNextAssessment(pm_uuid ?? null, company_uuid ?? null)

    await nextTick()

    if (nextAssessmentName.value) {
      globalActor.send({
        type: `GLOBAL.REDIRECT.${nextAssessmentName.value.toUpperCase()}` as GlobalMachineEvent,
      })
    } else {
      useCookies().remove(COOKIES.PM_UUID)
      useCookies().remove(COOKIES.COMPANY_UUID)
      globalActor.send({ type: "GLOBAL.MOVE.DONE" })
    }
  },
})
